import { Injectable } from '@angular/core';
import { InteractiveChartData, InteractiveChartBin, ChartPoint } from '../models/chart';

/**
 * Service for processing raw column data into chart-ready data structures
 * Handles client-side data processing for interactive charts
 */
@Injectable({
  providedIn: 'root'
})
export class ChartProcessingService {

  /**
   * Detect if a value is a date string or Date object
   */
  private isDateValue(value: any): boolean {
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }
    if (typeof value === 'string') {
      // Check common date patterns
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}/, // ISO date: 2024-01-15
        /^\d{2}\/\d{2}\/\d{4}/, // US date: 01/15/2024
        /^\d{2}-\d{2}-\d{4}/, // EU date: 15-01-2024
      ];
      if (datePatterns.some(pattern => pattern.test(value))) {
        const parsed = new Date(value);
        return !isNaN(parsed.getTime());
      }
    }
    return false;
  }

  /**
   * Convert a value to appropriate chart value (number or Date timestamp)
   */
  private convertToChartValue(value: any): number {
    if (value == null) {
      return NaN;
    }
    if (this.isDateValue(value)) {
      const date = value instanceof Date ? value : new Date(value);
      return date.getTime(); // Return timestamp in milliseconds
    }
    return Number(value);
  }

  /**
   * Process raw column data into chart data based on chart type
   * @param chartType Type of chart to generate data for
   * @param data Raw data rows (array of objects)
   * @param xColumn Name of the X-axis column
   * @param yColumn Optional name of the Y-axis column
   * @returns Processed chart data ready for rendering
   */
  processChartData(
    chartType: string,
    data: any[],
    xColumn: string,
    yColumn?: string
  ): InteractiveChartData {
    if (!data || data.length === 0) {
      return {};
    }

    switch (chartType) {
      case 'hist':
      case 'histogram':
        return this.processHistogram(data, xColumn);
      case 'bar':
        return this.processBarChart(data, xColumn);
      case 'kde':
        return this.processKDE(data, xColumn);
      case 'scatter':
        if (!yColumn) {
          throw new Error('Scatter plot requires a Y column');
        }
        return this.processScatter(data, xColumn, yColumn);
      case 'iqr':
        return this.processIQR(data, xColumn, yColumn);
      default:
        console.warn(`Unknown chart type: ${chartType}, defaulting to histogram`);
        return this.processHistogram(data, xColumn);
    }
  }

  /**
   * Process histogram data by creating bins
   */
  private processHistogram(data: any[], column: string): InteractiveChartData {
    const values = data.map(row => row[column]).filter(v => v != null);
    
    // Check if data is numeric or categorical
    const isNumeric = values.every(v => typeof v === 'number' || !isNaN(Number(v)));
    
    if (isNumeric) {
      // Create numeric bins
      const numericValues = values.map(v => Number(v));
      const bins = this.createNumericBins(numericValues);
      return { bins };
    } else {
      // Create categorical counts
      const counts = this.createCategoricalCounts(values);
      return {
        labels: counts.map(c => c.label),
        values: counts.map(c => c.value)
      };
    }
  }

  /**
   * Process bar chart (top 10 distinct values)
   */
  private processBarChart(data: any[], column: string): InteractiveChartData {
    const values = data.map(row => row[column]).filter(v => v != null);
    const counts = this.createCategoricalCountsByFrequency(values);
    
    // Take top 10
    const top10 = counts.slice(0, 10);
    
    return {
      labels: top10.map(c => c.label),
      values: top10.map(c => c.value)
    };
  }

  /**
   * Process KDE (Kernel Density Estimation) - simplified version
   */
  private processKDE(data: any[], column: string): InteractiveChartData {
    // Detect if the column contains dates
    let xIsDate = false;
    for (const row of data.slice(0, 10)) {
      if (row[column] != null && this.isDateValue(row[column])) {
        xIsDate = true;
        break;
      }
    }

    const values = data.map(row => row[column]).filter(v => v != null).map(v => this.convertToChartValue(v));
    
    if (values.length === 0) {
      return {};
    }

    // Sort values
    values.sort((a, b) => a - b);
    
    // Create smooth distribution using simple moving average
    const points = 100;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const step = (max - min) / points;
    
    const x: number[] = [];
    const y: number[] = [];
    
    for (let i = 0; i <= points; i++) {
      const xVal = min + i * step;
      x.push(xVal);
      
      // Simple density estimation: count values within bandwidth
      const bandwidth = (max - min) / 20;
      const count = values.filter(v => Math.abs(v - xVal) <= bandwidth).length;
      y.push(count / values.length / bandwidth);
    }
    
    return { x, y, xIsDate };
  }

  /**
   * Process scatter plot data
   */
  private processScatter(data: any[], xColumn: string, yColumn: string): InteractiveChartData {
    const points: ChartPoint[] = [];
    let xIsDate = false;
    let yIsDate = false;
    
    // Detect if columns contain dates by sampling first non-null values
    for (const row of data.slice(0, 10)) {
      if (!xIsDate && row[xColumn] != null && this.isDateValue(row[xColumn])) {
        xIsDate = true;
      }
      if (!yIsDate && row[yColumn] != null && this.isDateValue(row[yColumn])) {
        yIsDate = true;
      }
      if (xIsDate && yIsDate) break;
    }
    
    data.forEach((row, originalIndex) => {
      const x = row[xColumn];
      const y = row[yColumn];
      
      if (x != null && y != null) {
        const xValue = this.convertToChartValue(x);
        const yValue = this.convertToChartValue(y);
        
        if (!isNaN(xValue) && !isNaN(yValue)) {
          points.push({
            x: xValue,
            y: yValue,
            index: originalIndex // Preserve original row index from source data
          });
        }
      }
    });
    
    return { 
      points,
      xIsDate,
      yIsDate
    };
  }

  /**
   * Process IQR (Interquartile Range) data
   */
  private processIQR(data: any[], column: string, groupColumn?: string): InteractiveChartData {
    // Detect if the column contains dates
    let yIsDate = false;
    for (const row of data.slice(0, 10)) {
      if (row[column] != null && this.isDateValue(row[column])) {
        yIsDate = true;
        break;
      }
    }

    if (!groupColumn) {
      // Single column IQR - show as box plot
      const values = data.map(row => row[column]).filter(v => v != null).map(v => this.convertToChartValue(v));
      const stats = this.calculateBoxPlotStats(values);
      
      return {
        boxplot: [stats],
        yIsDate
      };
    } else {
      // Grouped IQR - calculate stats per group
      const groups = new Map<string, number[]>();
      
      data.forEach(row => {
        const groupValue = String(row[groupColumn] ?? 'null');
        const value = row[column];
        
        if (value != null) {
          if (!groups.has(groupValue)) {
            groups.set(groupValue, []);
          }
          groups.get(groupValue)!.push(this.convertToChartValue(value));
        }
      });
      
      const boxplotData: any[] = [];
      const labels: string[] = [];
      
      groups.forEach((vals, group) => {
        const stats = this.calculateBoxPlotStats(vals);
        labels.push(group);
        boxplotData.push(stats);
      });
      
      return { labels, boxplot: boxplotData, yIsDate };
    }
  }

  /**
   * Create numeric bins for histogram
   */
  private createNumericBins(values: number[], binCount: number = 20): InteractiveChartBin[] {
    if (values.length === 0) return [];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binWidth = (max - min) / binCount;
    
    const bins: InteractiveChartBin[] = [];
    
    for (let i = 0; i < binCount; i++) {
      const binMin = min + i * binWidth;
      const binMax = min + (i + 1) * binWidth;
      const count = values.filter(v => v >= binMin && (i === binCount - 1 ? v <= binMax : v < binMax)).length;
      
      bins.push({
        label: `${binMin.toFixed(2)} - ${binMax.toFixed(2)}`,
        value: count,
        range: { min: binMin, max: binMax }
      });
    }
    
    return bins;
  }

  /**
   * Create categorical value counts
   * Sorts by label (value/bucket) for histograms, not by frequency
   */
  private createCategoricalCounts(values: any[]): Array<{ label: string; value: number }> {
    const counts = new Map<string, number>();
    
    values.forEach(v => {
      const key = String(v);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    
    const result = Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
    
    // Sort by label (value/bucket) for proper histogram ordering
    // Try to sort numerically if possible, otherwise alphabetically
    result.sort((a, b) => {
      const aNum = Number(a.label);
      const bNum = Number(b.label);
      
      // If both are numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      
      // Otherwise, sort alphabetically
      return a.label.localeCompare(b.label);
    });
    
    return result;
  }

  /**
   * Create categorical value counts sorted by frequency
   * Used for "Top 10" bar charts where we want highest counts first
   */
  private createCategoricalCountsByFrequency(values: any[]): Array<{ label: string; value: number }> {
    const counts = new Map<string, number>();
    
    values.forEach(v => {
      const key = String(v);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    
    const result = Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
    
    // Sort by count descending
    result.sort((a, b) => b.value - a.value);
    
    return result;
  }

  /**
   * Calculate box plot statistics (5-number summary)
   */
  private calculateBoxPlotStats(values: number[]): {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  } {
    if (values.length === 0) {
      return { min: 0, q1: 0, median: 0, q3: 0, max: 0 };
    }
    
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    
    return {
      min: sorted[0],
      q1: this.percentile(sorted, 25),
      median: this.percentile(sorted, 50),
      q3: this.percentile(sorted, 75),
      max: sorted[n - 1]
    };
  }

  /**
   * Calculate percentile value
   */
  private percentile(sortedValues: number[], p: number): number {
    const index = (p / 100) * (sortedValues.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;
    
    return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
  }
}
